import { useI18n } from '@i18n';
import { SxProps, TextField, TextFieldProps, Theme } from '@mui/material';
import { language_enum } from '@predium/enums';
import isNil from 'lodash/isNil';
import { ReactNode } from 'react';
import NumberFormat, { NumberFormatProps, NumberFormatValues } from 'react-number-format';
interface IProps {
  label?: ReactNode;
  value?: number | null;
  onValueChange?: (values: NumberFormatValues) => void;
  min?: number;
  max?: number;
  size?: TextFieldProps['size'];
  sx?: SxProps<Theme>;
  disabled?: boolean;
  variant?: TextFieldProps['variant'];
  placeholder?: string;
  decimalScale?: number;
  helperText?: string;
  autoFocus?: boolean;
  numberFormatProps?: NumberFormatProps<TextFieldProps>;
  allowDecimals?: boolean;
  maxDecimalPlaces?: number;
  autoComplete?: TextFieldProps['autoComplete'];
  isError?: boolean;
}

type NumberFieldProps = IProps & Pick<TextFieldProps, 'InputProps' | 'onChange'>;

export default function NumberField({
  value,
  label,
  size = 'medium',
  disabled = false,
  min,
  max = Number.MAX_SAFE_INTEGER,
  sx,
  variant,
  placeholder,
  helperText,
  onValueChange,
  allowDecimals = true,
  maxDecimalPlaces = 3,
  autoComplete = 'on',
  isError = false,
  ...other
}: NumberFieldProps) {
  const { language } = useI18n();

  const decimalSeparator = language === language_enum.deDE ? ',' : '.';
  const thousandSeparator = language === language_enum.deDE ? '.' : ',';

  return (
    <NumberFormat
      value={value !== null ? value : ''}
      onValueChange={(values, _) => {
        if (onValueChange) {
          onValueChange(values);
        }
      }}
      size={size}
      variant={variant}
      customInput={TextField}
      autoComplete={autoComplete}
      label={label}
      sx={sx}
      disabled={disabled}
      placeholder={placeholder}
      helperText={helperText}
      error={isError}
      allowedDecimalSeparators={allowDecimals ? [',', '.'] : undefined}
      decimalSeparator={allowDecimals ? decimalSeparator : undefined}
      thousandSeparator={allowDecimals ? thousandSeparator : false}
      decimalScale={allowDecimals ? maxDecimalPlaces : 0} // Default to 3 decimal places if decimals are allowed
      allowNegative={false}
      onChange={() => {
        //this is intentionally left blank since the value is handled by onValueChange
      }}
      isAllowed={(values) => {
        const { floatValue, formattedValue } = values;
        return formattedValue === '' || (!isNil(floatValue) && floatValue >= 0 && floatValue <= max);
      }}
      {...other}
    />
  );
}
